import type { SessionType, Settings, TimerState } from './types';

export function getSessionDuration(type: SessionType, settings: Settings): number {
  switch (type) {
    case 'work':       return settings.workDuration * 60;
    case 'shortBreak': return settings.shortBreakDuration * 60;
    case 'longBreak':  return settings.longBreakDuration * 60;
  }
}

export function getNextSessionType(current: SessionType, pomodorosThisSet: number, settings: Settings): SessionType {
  if (current !== 'work') return 'work';
  if ((pomodorosThisSet + 1) % settings.longBreakAfter === 0) return 'longBreak';
  return 'shortBreak';
}

export function formatTime(seconds: number): string {
  const m = Math.floor(Math.abs(seconds) / 60);
  const s = Math.abs(seconds) % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

/** Remaining seconds for countdown display */
export function getRemainingSeconds(state: TimerState): number {
  return Math.max(0, state.duration - state.elapsed);
}

/** Progress 0-1 */
export function getProgress(state: TimerState): number {
  if (state.duration === 0) return 0;
  return Math.min(1, state.elapsed / state.duration);
}

// ─── Chrome alarms helpers (extension context only) ─────────────────────────

export const ALARM_TIMER  = 'flowfocus-timer';
export const ALARM_NOTIFY = 'flowfocus-notify';

/** Schedule the main timer alarm */
export function scheduleTimerAlarm(durationSeconds: number) {
  if (typeof chrome === 'undefined' || !chrome.alarms) return;
  chrome.alarms.create(ALARM_TIMER, {
    delayInMinutes: durationSeconds / 60,
  });
}

/** Schedule an early-warning notification alarm */
export function scheduleNotifyAlarm(durationSeconds: number, minutesBefore: number) {
  if (typeof chrome === 'undefined' || !chrome.alarms) return;
  const delay = durationSeconds / 60 - minutesBefore;
  if (delay <= 0) return;
  chrome.alarms.create(ALARM_NOTIFY, { delayInMinutes: delay });
}

export function clearTimerAlarms() {
  if (typeof chrome === 'undefined' || !chrome.alarms) return;
  chrome.alarms.clear(ALARM_TIMER);
  chrome.alarms.clear(ALARM_NOTIFY);
}

/** Send a chrome notification */
export function sendChromeNotification(title: string, message: string, id = 'flowfocus') {
  if (typeof chrome === 'undefined' || !chrome.notifications) return;
  chrome.notifications.create(id, {
    type: 'basic',
    iconUrl: '/icons/icon128.png',
    title,
    message,
    priority: 2,
  });
}

/** Update the extension badge with remaining minutes */
export function updateBadge(remainingSeconds: number, status: 'running' | 'paused' | 'idle') {
  if (typeof chrome === 'undefined' || !chrome.action) return;
  if (status === 'idle') {
    chrome.action.setBadgeText({ text: '' });
    return;
  }
  const m = Math.ceil(remainingSeconds / 60);
  chrome.action.setBadgeText({ text: String(m) });
  chrome.action.setBadgeBackgroundColor({
    color: status === 'running' ? '#FF6B6B' : '#AEAEB2',
  });
}

/** Día local AAAA-MM-DD (no UTC: toISOString cambia de día a las 22:00/23:00 en España). */
export function localDateKey(d: Date | number = new Date()): string {
  const x = typeof d === 'number' ? new Date(d) : d;
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}
